import {
  Form, Input, Typography, Select,
} from 'antd';
import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { FormContainer } from '../../../components/Container';
import FooterNavigator from '../../../components/FooterNavigator';
import { PATIENT_DETAILS, MEDICARE } from '../../../constants/routes';
import {
  addAttachmentsData,
  addBookingQuestionAndAnswersData,
  selectBookingAttachmentsData, selectBookingData,
  selectBookingQuestionAndAnswersData,
} from '../../../redux/slices/booking';
import { useAppDispatch } from '../../../hooks/redux-hooks';
import UploadForm from '../../../components/UploadForm';
import { HealthSummaryQuestionMapping } from '../../../model';
import analytics from '../../../infra/analytics';

const { Text } = Typography;
const { TextArea } = Input;

const DocumentsUploadingForm = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const bookingData = useSelector(selectBookingData);
  const {
    healthSummary, medicalSupporting, additionalReport1, additionalReport2,
  } = useSelector(selectBookingAttachmentsData);
  const { healthConcern, gpName, medibankMemberNo } = useSelector(selectBookingQuestionAndAnswersData);

  const handleUploadHealthSummary = useCallback((attachment) => dispatch(addAttachmentsData({ healthSummary: attachment })), [dispatch]);
  const handleUploadMedicalSupporting = useCallback((attachment) => dispatch(addAttachmentsData({ medicalSupporting: attachment })), [dispatch]);
  const handleUploadAdditionalReport1 = useCallback((attachment) => dispatch(addAttachmentsData({ additionalReport1: attachment })), [dispatch]);
  const handleUploadAdditionalReport2 = useCallback((attachment) => dispatch(addAttachmentsData({ additionalReport2: attachment })), [dispatch]);

  const [selectedSource, setSelectedSource] = useState(gpName || '');

  useEffect(() => {
    form.setFieldsValue({ healthConcern, gpName, medibankMemberNo });
  }, [form, healthConcern, gpName, medibankMemberNo]);

  const handleSourceChange = useCallback((value) => {
    setSelectedSource(value);
  }, []);

  const submit = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (values.gpName !== 'medibank' && values.medibankMemberNo) {
        values.medibankMemberNo = undefined;
      }

      dispatch(addBookingQuestionAndAnswersData(values));
      return true;
    } catch (e) {
      analytics.trackVpcFormError({
        form_id: bookingData.formId,
        error_type: 'form_validation_error',
        page_path: pathname,
      });

      return false;
    }
  }, [bookingData.formId, dispatch, form, pathname]);
  return (
    <>
      <FormContainer layout="vertical" form={form}>
        <Form.Item
          label="If you have a referral or health summary from your GP please upload it here:"
        >
          <UploadForm uploadInfo={healthSummary} setUploadInfo={handleUploadHealthSummary} />
          <div>
            If you do not have a valid GP referral,
            <Text strong>{' a Medicare rebate will not be available.'}</Text>
          </div>
        </Form.Item>

        <Form.Item
          label="If you have any medical supporting documents (GP care plan, mental health summary, historical psychiatric reports, allied health reports, and other medical summaries) please upload here:"
        >
          <UploadForm uploadInfo={medicalSupporting} setUploadInfo={handleUploadMedicalSupporting} />
        </Form.Item>

        <Form.Item
          label="If you have any other medical reports please upload here:"
        >
          <UploadForm uploadInfo={additionalReport1} setUploadInfo={handleUploadAdditionalReport1} />
        </Form.Item>

        <Form.Item
          label="If you have any other medical summaries please upload here:"
        >
          <UploadForm uploadInfo={additionalReport2} setUploadInfo={handleUploadAdditionalReport2} />
        </Form.Item>

        <Form.Item
          name="gpName"
          required
          rules={[
            {
              required: true,
              message: 'Please select how you heard about our service.',
            },
          ]}
          label={HealthSummaryQuestionMapping.gpName}
        >
          <Select onChange={handleSourceChange}>
            <Select.Option value="medibank">Medibank</Select.Option>
            <Select.Option value="myhealth">MyHealth GP Practice</Select.Option>
            <Select.Option value="ahm">AHM</Select.Option>
            <Select.Option value="others">Others</Select.Option>
          </Select>
        </Form.Item>

        {selectedSource === 'medibank' && (
          <Form.Item
            name="medibankMemberNo"
            label={HealthSummaryQuestionMapping.medibankMemberNo}
            required
            rules={[
              {
                required: true,
                message: 'Please input your Medibank member number.',
              },
            ]}
          >
            <Input placeholder="Enter your Medibank member number" />
          </Form.Item>
        )}

        <Form.Item
          name="healthConcern"
          label={HealthSummaryQuestionMapping.healthConcern}
        >
          <TextArea rows={4} />
        </Form.Item>

        <FooterNavigator submit={submit} error="" next={MEDICARE} back={PATIENT_DETAILS} containerStyle={{ marginTop: '50px' }} />
      </FormContainer>
    </>
  );
};

export default DocumentsUploadingForm;
